import { createAbsoluteUrl, isLocalDevHost } from "utils/constants";

const OVERLAY_ID = "vle-external-redirect-overlay";

function resolveNavigationUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl) || pathOrUrl.startsWith("//")) {
    return pathOrUrl;
  }
  return createAbsoluteUrl(pathOrUrl);
}

export function isCrossOriginUrl(url: string): boolean {
  if (typeof window === "undefined") return true;
  try {
    const target = new URL(url, window.location.href);
    return target.origin !== window.location.origin;
  } catch {
    return true;
  }
}

/** Immediate blank surface so SPA chrome cannot flash during an external redirect. */
export function showExternalRedirectBlank(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(OVERLAY_ID)) return;

  const overlay = document.createElement("div");
  overlay.id = OVERLAY_ID;
  overlay.setAttribute("aria-busy", "true");
  overlay.setAttribute("aria-live", "polite");
  overlay.style.cssText =
    "position:fixed;inset:0;z-index:2147483647;background:#ffffff;margin:0;padding:0;";
  document.body.appendChild(overlay);

  const root = document.getElementById("root");
  if (root) {
    root.setAttribute("aria-hidden", "true");
    root.style.visibility = "hidden";
  }
}

/**
 * Navigate to a marketing-site path or absolute URL.
 * - Localhost / same-origin: normal assign (in-app remains usable).
 * - Cross-origin (dev/staging/prod marketing host): blank immediately, then replace.
 */
export function redirectToAbsoluteUrl(pathOrUrl: string = ""): void {
  if (typeof window === "undefined") return;

  const url = resolveNavigationUrl(pathOrUrl);

  if (isLocalDevHost() || !isCrossOriginUrl(url)) {
    window.location.assign(url);
    return;
  }

  showExternalRedirectBlank();
  window.location.replace(url);
}

/**
 * Capture left-clicks on cross-origin `<a href>` so navigation blanks first.
 * Skips new-tab / modified clicks and non-http(s) schemes.
 */
export function installExternalAnchorRedirectGuard(): () => void {
  if (typeof document === "undefined") return () => undefined;

  const onClick = (event: MouseEvent) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const anchor = (event.target as Element | null)?.closest?.(
      "a[href]",
    ) as HTMLAnchorElement | null;
    if (!anchor) return;

    const href = anchor.getAttribute("href");
    if (!href || href.startsWith("#")) return;
    if (/^(mailto:|tel:|sms:|javascript:)/i.test(href)) return;

    const target = (anchor.getAttribute("target") || "").toLowerCase();
    if (target === "_blank" || anchor.hasAttribute("download")) return;

    let absolute: string;
    try {
      absolute = new URL(href, window.location.href).href;
    } catch {
      return;
    }

    if (!isCrossOriginUrl(absolute)) return;

    event.preventDefault();
    redirectToAbsoluteUrl(absolute);
  };

  document.addEventListener("click", onClick, true);
  return () => document.removeEventListener("click", onClick, true);
}
